import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { FileType } from '../../../models/fileType';
import { Invoice } from '../../../models/invoice';
import { Talent } from '../../../models/talent';
import { INVOICES } from '../../../router/constants';
import { fileService } from '../../../services/fileService';
import { invoiceService } from '../../../services/invoiceService';
import { projectService } from '../../../services/projectService';
import { talentService } from '../../../services/talentService';
import InvoiceForm from '../InvoiceForm';
import { managerService } from '../../../services/managerService';
import { Manager } from '../../../models/manager';

const schema = yup.object({
  projectTitle: yup.string().default('').required(),
  companyName: yup.string().default('').required(),
  talentFullName: yup.string().default('').required(),
  manager: yup.object().default(null).required(),
  description: yup.string().default('').nullable(),
  address: yup.string().default('').nullable(),
  category: yup.string().default(''),
  expenseCode: yup.string().default(''),
  expenses: yup.number().default(null).nullable(),
  initials: yup.string().default('').nullable(),
  talentNotes: yup.string().default('').nullable(),
  id: yup.number().default(0),
  status: yup.string().default(''),
  createdOn: yup.string().default(''),
  total: yup
    .number()
    .default(null)
    .test('has hourlyRate and totalHours', '', (value, context) => {
      return !(
        !context.parent.hourlyRate &&
        !context.parent.totalHours &&
        !value
      );
    })
    .nullable(),
  hourlyRate: yup
    .number()
    .default(null)
    .test('has totalHours', '', (value, context) => {
      return !(!value && context.parent.totalHours);
    })
    .nullable(),
  totalHours: yup
    .number()
    .default(null)
    .test('has hourlyRate', '', (value, context) => {
      return !(!value && context.parent.hourlyRate);
    })
    .nullable()
});

type DraftInvoiceForm = yup.InferType<typeof schema>;

const defaultValues = schema.getDefault();

const DraftInvoice = (): JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [user, setUser] = useState<Talent | null>(null);
  const [managerList, setManagerList] = useState<Manager[] | undefined>();
  const [isProjectInvoice, setIsProjectInvoice] = useState<boolean>(false);
  const [invoiceFiles, setInvoiceFiles] = useState<FileType[]>();
  const [invoiceFilesToDelete, setInvoiceFilesToDelete] =
    useState<FileType[]>();

  const methods = useForm<DraftInvoiceForm>({
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur'
  });

  const { setValue, handleSubmit } = methods;

  useEffect(() => {
    if (params.id) {
      getInvoice(params.id);
    }
    getUser();
  }, []);

  useEffect(() => {
    if (invoice) {
      setValue('projectTitle', invoice.projectTitle);
      setValue('companyName', invoice.companyName ?? '');
      setValue('manager', invoice.manager);
      setValue('talentFullName', invoice.talentFullName);
      setValue('expenses', invoice.expenses);
      setValue('category', invoice.category);
      setValue('expenseCode', invoice.expenseCode);
      setValue('address', invoice.address ?? '');
      if (invoice.hourlyRate && invoice.totalHours) {
        setValue('hourlyRate', invoice.hourlyRate);
        setValue('totalHours', invoice.totalHours);
      } else {
        setValue('total', invoice.total);
      }
      setValue('description', invoice.description ?? '');
      setValue('talentNotes', invoice.talentNotes ?? '');
      setValue('id', invoice.id);
      setValue('status', invoice.status ?? '');
      setInvoiceFiles(invoice.attachments);
    } else if (user) {
      setValue('companyName', user.company.name ?? '');
      setValue('talentFullName', `${user?.firstName} ${user?.lastName}`);
    }
  }, [invoice, user]);

  const getInvoice = (id: string) => {
    invoiceService.getTalentInvoiceById(+id).then((response) => {
      setInvoice(response.data);
      if (response.data.projectId) {
        setIsProjectInvoice(true);
      } else {
        getManagerList();
      }
    });
  };

  const getUser = () => {
    talentService.getCurrentTalent().then((response) => {
      setUser(response.data);
    });
  };

  const getManagerList = () => {
    managerService.getManagerList().then((response) => {
      setManagerList(response.data);
    });
  };

  const handleCreateInvoice = (
    data: DraftInvoiceForm,
    files: File[],
    isDraft = false
  ) => {
    const output = {
      ...data,
      projectId: invoice?.projectId,
      hourlyRate: data.hourlyRate ?? null,
      totalHours: data.totalHours ?? null
    } as Invoice;

    invoiceService
      .updateInvoice({ data: output, submit: !isDraft })
      .then((response) => {
        navigate(INVOICES);
        if (files.length && response?.data?.id) {
          const formDataFiles = new FormData();
          files.forEach((file) => {
            formDataFiles.append('files', file);
          });
          invoiceService
            .saveProjectFiles(response.data.id, formDataFiles)
            .then(() => navigate(INVOICES));
        }
        if (invoiceFilesToDelete?.length) {
          invoiceFilesToDelete.forEach((file) => {
            fileService.deleteFileById(file.uuid);
          });
        }
      })
      .finally(() => {
        if (params?.closeProject === 'close' && invoice?.projectId) {
          projectService.closeProjectById(invoice?.projectId);
        }
      });
  };

  return (
    <>
      <PageTitle>Create Invoice</PageTitle>
      <FormProvider {...methods}>
        <form>
          <InvoiceForm
            title={`Draft Invoice #${invoice?.id}`}
            projectAttachments={invoice?.projectAttachments}
            invoiceFiles={invoiceFiles}
            managerList={managerList}
            isProjectInvoice={isProjectInvoice}
            setInvoiceFiles={setInvoiceFiles}
            setInvoiceFilesToDelete={setInvoiceFilesToDelete}
            handleSubmit={(files: File[], isDraft?: boolean) =>
              handleSubmit((data) =>
                handleCreateInvoice(data, files, isDraft)
              )()
            }
          />
        </form>
      </FormProvider>
    </>
  );
};

export default DraftInvoice;
